import * as React from "react";
import {
  Award,
  Building2,
  Globe,
  KeyRound,
  Mail,
  Phone,
  PhoneOff,
  Plus,
  Bell,
  RotateCcw,
  Save,
  Shield,
  User,
  UserPlus,
} from "lucide-react";
import { Text } from "../../../../ui/components/Text";
import { Button } from "../../../../ui/components/Button";
import { Callout } from "../../../../ui/components/Callout";
import { Modal } from "../../../../ui/components/Modal";
import { Avatar } from "../../../../ui/components/Avatar";
import { EmailInput, PasswordInput, TextInput } from "../../../../ui/components/Form";
import { PasswordStrengthBar, pwScore } from "../../../../ui/components/Form/PasswordStrength";
import { CollapsibleSection } from "../../../../ui/components/CollapsibleSection";
import { UnsavedChangesChip } from "../../../../ui/components/UnsavedChangesChip";
import { ListView } from "../../../../ui/components/ListView";
import { ListViewOrganizationCard } from "../../../../ui/components/ListView/components/ListViewOrganizationCard";
import type { FloatingAction } from "../../../../ui/components/FloatingActionBar";
import type { ListParams } from "../../../../ui/api/listApi.types";
import { useAuth } from "../../../lib/auth/hooks";
import { hasPermissionInPayload } from "../../../lib/auth/permissions";
import { useI18n } from "../../../lib/i18n";
import * as userApi from "../../../lib/api/users";
import { getApiErrorMessage } from "../../../lib/api/api-error";
import { ProfileAvatarEditor } from "../../profile/ProfileAvatarEditor";
import { normalizeAvatarUrl } from "../../../lib/profile-avatar-image";
import { CreateOrganizationModal } from "../../profile/CreateOrganizationModal";
import { JoinOrganizationModal } from "../../profile/JoinOrganizationModal";
import { clearInviteToken } from "../../../lib/auth/invite-pending";
import { VerifyEmailModal } from "../../profile/VerifyEmailModal";
import { VerifyPhoneModal, type VerifyPhoneModalMode } from "../../profile/VerifyPhoneModal";
import { UserAccountDangerZone } from "../../users/UserAccountDangerZone";
import { UserCertificationsSection } from "../../users/UserCertificationsSection";
import { UserSubscriptionsSection } from "../../users/UserSubscriptionsSection";
import { ProductSubscriptionsModal } from "../../products/ProductSubscriptionsModal";
import {
  LocalePreferencesSection,
  type LocalePreferencesController,
} from "../../profile/LocalePreferencesSection";
import { useSectionCollapse } from "../../../hooks/useSectionCollapse";
import { DetailPageLayout } from "../DetailPageLayout";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import * as styles from "./ProfilePage.style.css";
import * as confirmStyles from "../../organizations/OrganizationDetailPage.css";

const COUNTRY_OPTIONS = [
  { code: "1", label: "🇺🇸 +1" },
  { code: "44", label: "🇬🇧 +44" },
  { code: "1", label: "🇨🇦 +1" },
] as const;

const SECTION_IDS = {
  account: "profile-account",
  contact: "profile-contact",
  locale: "profile-locale",
  orgs: "profile-orgs",
  certifications: "profile-certifications",
  subscriptions: "profile-subscriptions",
  security: "profile-security",
} as const;

function formatPhoneDisplay(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

function VerificationBadge({
  verified,
  channel,
}: {
  verified: boolean;
  channel: "email" | "phone";
}) {
  const { tx } = useI18n();
  const Icon = channel === "email" ? Mail : Phone;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 12,
        fontWeight: 600,
        padding: "2px 10px 2px 8px",
        borderRadius: 9999,
        background: verified ? "#DEF7EC" : "#FEE2E2",
        color: verified ? "#03543F" : "#991B1B",
      }}
    >
      <Icon size={12} aria-hidden strokeWidth={2.5} />
      {verified ? tx("pro.profile.verified") : tx("pro.profile.notVerified")}
    </span>
  );
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export function ProfilePage() {
  const { user, accessToken, userPayload, refreshUser, refreshAccessToken } = useAuth();
  const { t, tx } = useI18n();
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("profile");

  const certOrganizationId = React.useMemo(() => {
    const activeOrgId = userPayload?.org_id;
    if (activeOrgId && user?.organizations?.some((o) => o.id === activeOrgId)) {
      return activeOrgId;
    }
    const primary = user?.organizations?.find((o) => o.isPrimary)?.id;
    return primary ?? user?.organizations?.[0]?.id;
  }, [user?.organizations, userPayload?.org_id]);

  const canEnrollCertifications = hasPermissionInPayload(
    userPayload,
    "certification:enroll",
  );
  const canViewCertifications = hasPermissionInPayload(
    userPayload,
    "certification:view",
  );
  const canManageCertifications = hasPermissionInPayload(
    userPayload,
    "certification:manage",
  );

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [avatarUrl, setAvatarUrl] = React.useState("");
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [profileMessage, setProfileMessage] = React.useState<string | null>(null);
  const [profileError, setProfileError] = React.useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordSaving, setPasswordSaving] = React.useState(false);
  const [passwordMessage, setPasswordMessage] = React.useState<string | null>(null);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);

  const [createOrgModalOpen, setCreateOrgModalOpen] = React.useState(false);
  const [joinOrgModalOpen, setJoinOrgModalOpen] = React.useState(false);
  const [verifyEmailModalOpen, setVerifyEmailModalOpen] = React.useState(false);
  const [verifyPhoneModalOpen, setVerifyPhoneModalOpen] = React.useState(false);
  const [verifyPhoneModalMode, setVerifyPhoneModalMode] =
    React.useState<VerifyPhoneModalMode>("verify");
  const [leaveOrgTarget, setLeaveOrgTarget] = React.useState<{
    id: string;
    name: string;
  } | null>(null);
  const [leaveOrgLoading, setLeaveOrgLoading] = React.useState(false);
  const [leaveOrgError, setLeaveOrgError] = React.useState<string | null>(null);
  const [preApprovedOrgs, setPreApprovedOrgs] = React.useState<
    userApi.OrganizationWithMembership[]
  >([]);
  const [orgActionOrgId, setOrgActionOrgId] = React.useState<string | null>(null);
  const [orgActionError, setOrgActionError] = React.useState<string | null>(null);
  const [subscriptionsModalOpen, setSubscriptionsModalOpen] = React.useState(false);
  const [subscriptionsProductId, setSubscriptionsProductId] = React.useState<string | undefined>();
  const [subscriptionsProductName, setSubscriptionsProductName] = React.useState("");
  const [subscriptionsRefreshKey, setSubscriptionsRefreshKey] = React.useState(0);
  const [localeMeta, setLocaleMeta] = React.useState({
    dirty: false,
    changeCount: 0,
    saving: false,
  });
  const localeCtrlRef = React.useRef<LocalePreferencesController | null>(null);

  const reloadPreApprovedOrgs = React.useCallback(async () => {
    if (!token || !user) {
      setPreApprovedOrgs([]);
      return;
    }
    try {
      const results = await userApi.fetchJoinableOrganizations(token);
      setPreApprovedOrgs(
        results.filter(
          (o) => o.isPreApproved && !o.isMember && !o.isPendingRequest,
        ),
      );
    } catch {
      setPreApprovedOrgs([]);
    }
  }, [token, user]);

  React.useEffect(() => {
    void reloadPreApprovedOrgs();
  }, [reloadPreApprovedOrgs]);

  const handleInstantJoinOrganization = React.useCallback(
    async (orgId: string) => {
      if (!token) return;
      setOrgActionError(null);
      setOrgActionOrgId(orgId);
      try {
        await userApi.instantJoinOrganization(token, orgId);
        clearInviteToken();
        await refreshAccessToken().catch(() => refreshUser());
        await reloadPreApprovedOrgs();
      } catch (e) {
        setOrgActionError(getApiErrorMessage(e, t("pro.profile.failedJoinOrg")));
      } finally {
        setOrgActionOrgId(null);
      }
    },
    [token, refreshAccessToken, refreshUser, reloadPreApprovedOrgs, t],
  );

  const openVerifyPhoneModal = React.useCallback((mode: VerifyPhoneModalMode) => {
    setVerifyPhoneModalMode(mode);
    setVerifyPhoneModalOpen(true);
  }, []);

  const handleConfirmLeaveOrganization = React.useCallback(async () => {
    if (!token || !leaveOrgTarget || !user?.id) return;
    setLeaveOrgLoading(true);
    setLeaveOrgError(null);
    try {
      await userApi.leaveOrganization(token, leaveOrgTarget.id, user.id);
      setLeaveOrgTarget(null);
      await refreshUser();
    } catch (e) {
      setLeaveOrgError(
        e instanceof Error ? e.message : t("pro.profile.failedLeaveOrg"),
      );
    } finally {
      setLeaveOrgLoading(false);
    }
  }, [token, leaveOrgTarget, user?.id, refreshUser, t]);

  const handleCancelMembershipRequest = React.useCallback(
    async (orgId: string) => {
      if (!token) return;
      try {
        await userApi.cancelOrganizationMembershipRequest(token, orgId);
        await refreshUser();
      } catch {
        // ignore — user can retry from setup flow
      }
    },
    [token, refreshUser],
  );

  type ProfileOrgEntry =
    | { key: string; kind: "active"; org: userApi.UserOrganization }
    | { key: string; kind: "pending"; org: userApi.UserOrganization }
    | { key: string; kind: "preapproved"; org: userApi.OrganizationWithMembership };

  const orgEntries = React.useMemo((): ProfileOrgEntry[] => {
    if (!user) return [];
    const memberIds = new Set(user.organizations.map((o) => o.id));
    const pendingOnly = (user.pendingOrganizations ?? []).filter((o) => !memberIds.has(o.id));
    return [
      ...preApprovedOrgs.map((org) => ({ key: `preapproved-${org.id}`, kind: "preapproved" as const, org })),
      ...pendingOnly.map((org) => ({ key: `pending-${org.id}`, kind: "pending" as const, org })),
      ...user.organizations.map((org) => ({ key: `active-${org.id}`, kind: "active" as const, org })),
    ];
  }, [user, preApprovedOrgs]);

  const fetchOrgEntries = React.useCallback(
    async (params: ListParams) => {
      let rows = orgEntries.map((e) => ({ ...e, id: e.key }));
      if (params.search?.trim()) {
        const q = params.search.toLowerCase();
        rows = rows.filter((e) => e.org.name.toLowerCase().includes(q));
      }
      return { data: rows, total: rows.length };
    },
    [orgEntries],
  );

  const orgListColumns = React.useMemo(
    () => [
      {
        id: "name",
        label: t("pro.profile.orgColumn"),
        render: (row: ProfileOrgEntry & { id: string }) => row.org.name,
      },
      {
        id: "status",
        label: t("pro.profile.statusColumn"),
        render: (row: ProfileOrgEntry & { id: string }) =>
          row.kind === "pending"
            ? t("pro.profile.statusPending")
            : row.kind === "preapproved"
              ? t("pro.profile.statusAvailable")
              : t("pro.profile.statusMember"),
      },
    ],
    [t],
  );

  React.useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  React.useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setAvatarUrl(normalizeAvatarUrl(user.avatarUrl));
  }, [user]);

  const savedFirstName = (user?.firstName ?? "").trim();
  const savedLastName = (user?.lastName ?? "").trim();
  const savedAvatarUrl = normalizeAvatarUrl(user?.avatarUrl);
  const firstNameChanged = firstName.trim() !== savedFirstName;
  const lastNameChanged = lastName.trim() !== savedLastName;
  const avatarChanged = normalizeAvatarUrl(avatarUrl) !== savedAvatarUrl;
  const accountDirty = firstNameChanged || lastNameChanged || avatarChanged;
  const accountChangeCount =
    (firstNameChanged ? 1 : 0) + (lastNameChanged ? 1 : 0) + (avatarChanged ? 1 : 0);

  const handlePersistAvatar = React.useCallback(
    async (url: string) => {
      if (!token) return;
      const normalized = normalizeAvatarUrl(url) || null;
      if (normalizeAvatarUrl(user?.avatarUrl) === normalizeAvatarUrl(url)) {
        await refreshUser();
        return;
      }
      await userApi.updateProfile(token, { avatarUrl: normalized });
      await refreshUser();
      setProfileError(null);
    },
    [token, user?.avatarUrl, refreshUser],
  );

  const resetAccount = React.useCallback(() => {
    if (!user) return;
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setAvatarUrl(normalizeAvatarUrl(user.avatarUrl));
    setProfileError(null);
    setProfileMessage(null);
  }, [user]);

  const saveAccount = React.useCallback(async () => {
    if (!token || !accountDirty) return;
    setProfileSaving(true);
    setProfileError(null);
    setProfileMessage(null);
    try {
      await userApi.updateProfile(token, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(avatarChanged
          ? { avatarUrl: normalizeAvatarUrl(avatarUrl) || null }
          : {}),
      });
      await refreshUser();
      setProfileMessage(t("pro.profile.updated"));
    } catch (err) {
      setProfileError(getApiErrorMessage(err, t("pro.profile.failedUpdate")));
      throw err;
    } finally {
      setProfileSaving(false);
    }
  }, [token, accountDirty, firstName, lastName, avatarChanged, avatarUrl, refreshUser, t]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (newPassword !== confirmPassword) {
      setPasswordError(t("pro.profile.passwordsMismatch"));
      return;
    }
    setPasswordSaving(true);
    setPasswordError(null);
    setPasswordMessage(null);
    try {
      await userApi.updatePassword(token, {
        currentPassword,
        newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage(t("pro.profile.passwordUpdated"));
    } catch (err) {
      setPasswordError(getApiErrorMessage(err, t("pro.profile.failedUpdatePassword")));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLocaleStateChange = React.useCallback((state: LocalePreferencesController) => {
    localeCtrlRef.current = state;
    setLocaleMeta((prev) => {
      if (
        prev.dirty === state.dirty &&
        prev.changeCount === state.changeCount &&
        prev.saving === state.saving
      ) {
        return prev;
      }
      return {
        dirty: state.dirty,
        changeCount: state.changeCount,
        saving: state.saving,
      };
    });
  }, []);

  const handleLocaleSaved = React.useCallback(async () => {
    await refreshUser();
  }, [refreshUser]);

  const localeDirty = localeMeta.dirty;
  const localeChangeCount = localeMeta.changeCount;
  const totalDirtyCount = accountChangeCount + localeChangeCount;
  const hasDirty = accountDirty || localeDirty;
  const saving = profileSaving || localeMeta.saving;

  const handleResetAll = React.useCallback(() => {
    resetAccount();
    localeCtrlRef.current?.reset();
  }, [resetAccount]);

  const handleSaveAll = React.useCallback(async () => {
    try {
      if (accountDirty) await saveAccount();
      if (localeCtrlRef.current?.dirty) await localeCtrlRef.current.save();
    } catch {
      /* errors surfaced in section callouts */
    }
  }, [accountDirty, saveAccount]);

  const floatingActions = React.useMemo((): FloatingAction[] => {
    if (!hasDirty) return [];
    return [
      {
        id: "reset",
        icon: <RotateCcw size={16} />,
        shape: "circle",
        tooltip: t("pro.productSettings.resetUnsaved"),
        disabled: saving,
        onClick: handleResetAll,
      },
      {
        id: "save",
        label:
          totalDirtyCount === 1
            ? `Save ${totalDirtyCount} change`
            : `Save ${totalDirtyCount} changes`,
        icon: <Save size={16} />,
        shape: "rectangle",
        loading: saving,
        disabled: saving,
        onClick: () => void handleSaveAll(),
      },
    ];
  }, [hasDirty, totalDirtyCount, saving, handleResetAll, handleSaveAll, t]);

  React.useEffect(() => {
    if (!hasDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasDirty]);

  if (!user) {
    return (
      <div className={styles.page}>
        <div className={styles.pageColumn}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>
            {tx("pro.profile.loading")}
          </Text>
        </div>
      </div>
    );
  }

  const displayCountryCode = user.countryCode ?? "1";
  const displayPhone = user.phone ?? "";
  const hasPhone = Boolean(user.phone?.trim());
  const displayName =
    [firstName, lastName].filter(Boolean).join(" ").trim() || user.email;
  const avatarSrc = normalizeAvatarUrl(avatarUrl) || undefined;

  const sectionLayout = bindSectionLayout([
      {
        id: SECTION_IDS.account,
        label: t("pro.profile.accountDetails"),
        icon: <User size={14} />,
      },
      {
        id: SECTION_IDS.contact,
        label: t("pro.profile.contactMethods"),
        icon: <Mail size={14} />,
      },
      ...(token
        ? [
            {
              id: SECTION_IDS.locale,
              label: t("pro.locale.title"),
              icon: <Globe size={14} />,
            },
          ]
        : []),
      {
        id: SECTION_IDS.orgs,
        label: t("pro.profile.organizations"),
        icon: <Building2 size={14} />,
      },
      ...(token
        ? [
            {
              id: SECTION_IDS.certifications,
              label: t("pro.profile.certifications"),
              icon: <Award size={14} />,
            },
            {
              id: SECTION_IDS.subscriptions,
              label: t("pro.users.subscriptions"),
              icon: <Bell size={14} />,
            },
          ]
        : []),
      {
        id: SECTION_IDS.security,
        label: t("pro.profile.security"),
        icon: <Shield size={14} />,
      },
    ]);

  return (
    <>
      <DetailPageLayout
        header={{
          media: (
            <Avatar
              email={user.email}
              firstName={firstName}
              lastName={lastName}
              image={avatarSrc}
              size={64}
            />
          ),
          title: displayName,
          subtitle: user.email,
          badges: [
            <VerificationBadge
              key="email"
              channel="email"
              verified={user.emailVerified}
            />,
            ...(hasPhone
              ? [
                  <VerificationBadge
                    key="phone"
                    channel="phone"
                    verified={user.phoneVerified}
                  />,
                ]
              : []),
          ],
        }}
        {...sectionLayout}
        footer={tx("pro.profile.memberSince", { date: formatDate(user.createdAt) })}
        floatingActions={floatingActions}
      >
        <CollapsibleSection
          id={SECTION_IDS.account}
          title={t("pro.profile.accountDetails")}
          icon={<User size={20} />}
          expanded={isExpanded(SECTION_IDS.account)}
          onToggleExpanded={() => toggle(SECTION_IDS.account)}
          highlight={accountDirty ? "accent" : false}
          statusIndicators={
            accountDirty
              ? [<UnsavedChangesChip key="u" count={accountChangeCount} />]
              : undefined
          }
        >
          <div className={styles.cardInner}>
            {profileError && <Callout variant="error">{profileError}</Callout>}
            {profileMessage && !accountDirty && (
              <Callout variant="success">{profileMessage}</Callout>
            )}

            <div className={styles.accountDetailsRow}>
              <div className={styles.accountDetailsFields}>
                <TextInput
                  label={t("pro.profile.firstName")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  changed={firstNameChanged}
                />
                <TextInput
                  label={t("pro.profile.lastName")}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  changed={lastNameChanged}
                />
              </div>
              <div className={styles.accountDetailsAvatar}>
                <ProfileAvatarEditor
                  email={user.email}
                  firstName={firstName}
                  lastName={lastName}
                  avatarUrl={avatarUrl}
                  googleAvatarUrl={user.googleAvatarUrl}
                  onAvatarUrlChange={setAvatarUrl}
                  onAvatarPersist={handlePersistAvatar}
                  accessToken={token}
                  changed={avatarChanged}
                />
              </div>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.contact}
          title={t("pro.profile.contactMethods")}
          icon={<Mail size={20} />}
          expanded={isExpanded(SECTION_IDS.contact)}
          onToggleExpanded={() => toggle(SECTION_IDS.contact)}
        >
          <div className={styles.cardInner}>
            <div>
              <div className={styles.fieldHeader}>
                <Text level="body-small" weight="bold">
                  {tx("pro.profile.email")}
                </Text>
                <VerificationBadge channel="email" verified={user.emailVerified} />
              </div>
              <div className={styles.emailTooltipWrapper}>
                <EmailInput
                  value={user.email}
                  disabled
                  readOnly
                  onChange={() => {}}
                />
                <span className={styles.emailTooltip}>
                  {tx("pro.profile.emailManagedHint")}
                </span>
              </div>
              <span
                className={styles.emailHintMobile}
                style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}
              >
                {tx("pro.profile.emailManagedHint")}
              </span>
              {!user.emailVerified && (
                <div className={styles.outlineActions}>
                  <Button
                    type="button"
                    color="primary"
                    variant="solid"
                    className={styles.outlineActionButton}
                    iconLeft={<Mail size={16} />}
                    onClick={() => setVerifyEmailModalOpen(true)}
                  >
                    {tx("pro.profile.verifyEmail")}
                  </Button>
                </div>
              )}
            </div>

            <div>
              <div className={styles.fieldHeader}>
                <Text level="body-small" weight="bold">
                  {tx("pro.profile.phone")}
                </Text>
                {hasPhone && (
                  <VerificationBadge channel="phone" verified={user.phoneVerified} />
                )}
              </div>
              {hasPhone ? (
                <>
                  <div className={authText.phoneRow} style={{ alignItems: "center" }}>
                    <select
                      className={authText.countrySelect}
                      value={displayCountryCode}
                      disabled
                      aria-label={t("pro.profile.countryCode")}
                    >
                      {COUNTRY_OPTIONS.map((opt, i) => (
                        <option key={`${opt.code}-${i}`} value={opt.code}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TextInput
                        value={formatPhoneDisplay(displayPhone)}
                        onChange={() => {}}
                        disabled
                        aria-label={t("pro.profile.phoneNumber")}
                      />
                    </div>
                  </div>
                  <div className={styles.outlineActions}>
                    <Button
                      type="button"
                      color="neutral"
                      variant="outline"
                      className={styles.outlineActionButton}
                      iconLeft={<PhoneOff size={16} />}
                      onClick={() => openVerifyPhoneModal("remove")}
                    >
                      {tx("pro.profile.removePhone")}
                    </Button>
                    {!user.phoneVerified && (
                      <Button
                        type="button"
                        color="primary"
                        variant="solid"
                        className={styles.outlineActionButton}
                        iconLeft={<Phone size={16} />}
                        onClick={() => openVerifyPhoneModal("verify")}
                      >
                        {tx("pro.profile.verifyPhone")}
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div className={styles.centeredEmpty}>
                  <p className={styles.centeredEmptyMessage}>
                    No phone number has been added.
                  </p>
                  <Button
                    type="button"
                    color="primary"
                    variant="solid"
                    iconLeft={<Phone size={16} />}
                    onClick={() => openVerifyPhoneModal("verify")}
                  >
                    Add phone number
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CollapsibleSection>

        {token ? (
          <CollapsibleSection
            id={SECTION_IDS.locale}
            title={t("pro.locale.title")}
            icon={<Globe size={20} />}
            expanded={isExpanded(SECTION_IDS.locale)}
            onToggleExpanded={() => toggle(SECTION_IDS.locale)}
            highlight={localeDirty ? "accent" : false}
            statusIndicators={
              localeDirty
                ? [<UnsavedChangesChip key="u" count={localeChangeCount} />]
                : undefined
            }
          >
            <LocalePreferencesSection
              mode="self"
              accessToken={token}
              preferredLanguage={user.preferredLanguage}
              preferredTimezone={user.preferredTimezone}
              hideChrome
              onStateChange={handleLocaleStateChange}
              onSaved={handleLocaleSaved}
            />
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection
          id={SECTION_IDS.orgs}
          title={t("pro.profile.organizations")}
          icon={<Building2 size={20} />}
          expanded={isExpanded(SECTION_IDS.orgs)}
          onToggleExpanded={() => toggle(SECTION_IDS.orgs)}
        >
          <div className={styles.cardInner}>
            {orgActionError && <Callout variant="error">{orgActionError}</Callout>}
            <ListView<ProfileOrgEntry & { id: string }>
              listId="profile-orgs"
              userId={user?.id ?? "anonymous"}
              columns={orgListColumns}
              fetchList={fetchOrgEntries}
              contextKey={String(orgEntries.length)}
              queryOptions={{ staleTime: Infinity }}
              enabledViews={["stack", "table"]}
              enableRowActions={false}
              searchPlaceholder={t("pro.profile.searchOrganizations")}
              emptyTitle={t("pro.profile.orgsEmptyTitle")}
              emptySubtitle={t("pro.profile.orgsEmptySubtitle")}
              getRowLabel={(row) => row.org.name}
              getRowCtas={(row) => {
                if (row.kind !== "preapproved") return undefined;
                return [
                  {
                    id: "join",
                    buttons: [
                      {
                        id: "join",
                        label: t("pro.profile.join"),
                        variant: "primary" as const,
                        disabled: orgActionOrgId === row.org.id,
                        onClick: () => void handleInstantJoinOrganization(row.org.id),
                      },
                    ],
                  },
                ];
              }}
              renderStackCard={(row, ctx) => {
                if (row.kind === "preapproved") {
                  return (
                    <ListViewOrganizationCard
                      key={row.id}
                      layout="stack"
                      organization={{ ...row.org, isPreApproved: true }}
                      actionsDisabled={orgActionOrgId === row.org.id}
                    />
                  );
                }
                if (row.kind === "pending") {
                  return (
                    <ListViewOrganizationCard
                      key={row.id}
                      layout="stack"
                      organization={{ ...row.org, isPendingRequest: true }}
                      contextActions={[
                        {
                          id: "cancel",
                          label: t("pro.profile.cancelRequest"),
                          destructive: true,
                          onClick: () => void handleCancelMembershipRequest(row.org.id),
                        },
                      ]}
                    />
                  );
                }
                return (
                  <ListViewOrganizationCard
                    key={row.id}
                    layout="stack"
                    organization={{ ...row.org, isMember: true }}
                    contextActions={[
                      {
                        id: "leave",
                        label: t("pro.profile.leaveOrganization"),
                        destructive: true,
                        onClick: () =>
                          setLeaveOrgTarget({ id: row.org.id, name: row.org.name }),
                      },
                    ]}
                    actionsDisabled={leaveOrgLoading}
                    onClick={ctx.onClick}
                  />
                );
              }}
            />
            <div className={styles.outlineActions}>
              <Button
                type="button"
                color="neutral"
                variant="outline"
                className={styles.outlineActionButton}
                iconLeft={<Plus size={16} />}
                onClick={() => setCreateOrgModalOpen(true)}
              >
                {tx("pro.profile.createOrganization")}
              </Button>
              <Button
                type="button"
                color="primary"
                variant="solid"
                className={styles.outlineActionButton}
                iconLeft={<UserPlus size={16} />}
                onClick={() => setJoinOrgModalOpen(true)}
              >
                {tx("pro.profile.joinOrganization")}
              </Button>
            </div>
          </div>
        </CollapsibleSection>

        {token ? (
          <CollapsibleSection
            id={SECTION_IDS.certifications}
            title={t("pro.profile.certifications")}
            icon={<Award size={20} />}
            expanded={isExpanded(SECTION_IDS.certifications)}
            onToggleExpanded={() => toggle(SECTION_IDS.certifications)}
          >
            <UserCertificationsSection
              userId={user.id}
              token={token}
              userLabel={t("pro.profile.you")}
              certificateName={
                [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
                user.email
              }
              selfView
              organizationId={certOrganizationId}
              canEnroll={canEnrollCertifications}
              canView={canViewCertifications || canEnrollCertifications}
              canManage={canManageCertifications}
            />
          </CollapsibleSection>
        ) : null}

        {token ? (
          <CollapsibleSection
            id={SECTION_IDS.subscriptions}
            title={t("pro.users.subscriptions")}
            icon={<Bell size={20} />}
            expanded={isExpanded(SECTION_IDS.subscriptions)}
            onToggleExpanded={() => toggle(SECTION_IDS.subscriptions)}
          >
            <div className={styles.cardInner}>
              <UserSubscriptionsSection
                userId={user.id}
                token={token}
                hideChrome
                refreshKey={subscriptionsRefreshKey}
                summaryPerspective="me"
                onEdit={(productId, productName) => {
                  setSubscriptionsProductId(productId);
                  setSubscriptionsProductName(productName);
                  setSubscriptionsModalOpen(true);
                }}
              />
            </div>
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection
          id={SECTION_IDS.security}
          title={t("pro.profile.security")}
          icon={<Shield size={20} />}
          expanded={isExpanded(SECTION_IDS.security)}
          onToggleExpanded={() => toggle(SECTION_IDS.security)}
        >
          <div className={styles.cardInner}>
            {user.googleId ? (
              <>
                <a
                  href="https://myaccount.google.com/signinoptions/password"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.fieldLink}
                  style={{ alignSelf: "flex-start" }}
                >
                  {tx("pro.profile.changePasswordOnGoogle")}
                </a>
                {token && (
                  <UserAccountDangerZone
                    mode="self"
                    userId={user.id}
                    userEmail={user.email}
                    userLabel={
                      [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
                      user.email
                    }
                    accessToken={token}
                    isDisabled={false}
                    canDisable
                    canHardDelete
                    canRestore={false}
                    embedded
                  />
                )}
              </>
            ) : (
              <>
                <form onSubmit={handleChangePassword}>
                  <div className={styles.cardInner}>
                    {passwordError && <Callout variant="error">{passwordError}</Callout>}
                    {passwordMessage && (
                      <Callout variant="success">{passwordMessage}</Callout>
                    )}

                    <PasswordInput
                      label={t("pro.profile.currentPassword")}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <PasswordInput
                      label={t("pro.profile.newPassword")}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    {newPassword && <PasswordStrengthBar pw={newPassword} />}
                    <PasswordInput
                      label={t("pro.profile.confirmNewPassword")}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />

                    <div className={`${styles.cardActions} ${styles.cardActionsEnd}`}>
                      <Button
                        type="submit"
                        color="primary"
                        variant="solid"
                        iconLeft={<KeyRound size={16} />}
                        loading={passwordSaving}
                        disabled={
                          !currentPassword ||
                          !newPassword ||
                          !confirmPassword ||
                          newPassword !== confirmPassword ||
                          pwScore(newPassword) < 2
                        }
                      >
                        {tx("pro.profile.updatePassword")}
                      </Button>
                    </div>
                  </div>
                </form>
                {token && (
                  <UserAccountDangerZone
                    mode="self"
                    userId={user.id}
                    userEmail={user.email}
                    userLabel={
                      [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
                      user.email
                    }
                    accessToken={token}
                    isDisabled={false}
                    canDisable
                    canHardDelete
                    canRestore={false}
                    embedded
                  />
                )}
              </>
            )}
          </div>
        </CollapsibleSection>
      </DetailPageLayout>

      <CreateOrganizationModal
        open={createOrgModalOpen}
        setOpen={setCreateOrgModalOpen}
        onCreated={() => refreshUser()}
      />

      <JoinOrganizationModal
        open={joinOrgModalOpen}
        setOpen={setJoinOrgModalOpen}
        user={user}
        onMembershipChange={() => void reloadPreApprovedOrgs()}
        onDone={() => {
          void refreshUser().then(() => reloadPreApprovedOrgs());
        }}
      />

      <VerifyEmailModal
        open={verifyEmailModalOpen}
        setOpen={setVerifyEmailModalOpen}
        onClose={() => {
          void refreshAccessToken()
            .then(() => reloadPreApprovedOrgs())
            .catch(() => void refreshUser());
        }}
      />

      <VerifyPhoneModal
        open={verifyPhoneModalOpen}
        setOpen={setVerifyPhoneModalOpen}
        mode={verifyPhoneModalMode}
        onClose={() => void refreshUser()}
      />

      <Modal
        open={leaveOrgTarget !== null}
        setOpen={(open) => {
          if (!open && !leaveOrgLoading) {
            setLeaveOrgTarget(null);
            setLeaveOrgError(null);
          }
        }}
        minWidth="420px"
        minHeight="auto"
        maxWidth="480px"
      >
        {leaveOrgTarget && (
          <div className={confirmStyles.confirmModalBody}>
            <h3 className={confirmStyles.confirmModalTitle}>
              {tx("pro.profile.leaveOrgModalTitle")}
            </h3>
            <p className={confirmStyles.confirmModalText}>
              {tx("pro.profile.leaveOrgConfirmPrefix")} {leaveOrgTarget.name}.
            </p>
            {leaveOrgError && <Callout variant="error">{leaveOrgError}</Callout>}
            <div className={confirmStyles.confirmModalActions}>
              <Button
                color="neutral"
                variant="outline"
                size="md"
                disabled={leaveOrgLoading}
                onClick={() => {
                  setLeaveOrgTarget(null);
                  setLeaveOrgError(null);
                }}
              >
                {tx("shared.common.cancel")}
              </Button>
              <Button
                color="danger"
                variant="solid"
                size="md"
                loading={leaveOrgLoading}
                onClick={() => void handleConfirmLeaveOrganization()}
              >
                {tx("pro.profile.leaveOrganization")}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {subscriptionsProductId ? (
        <ProductSubscriptionsModal
          open={subscriptionsModalOpen}
          setOpen={setSubscriptionsModalOpen}
          productId={subscriptionsProductId}
          productName={subscriptionsProductName}
          initialSubscriberUserId={user.id}
          initialSubscriberLabel={displayName}
          lockUser
          lockProduct
          summaryPerspective="me"
          onSaved={() => {
            setSubscriptionsRefreshKey((k) => k + 1);
          }}
        />
      ) : null}
    </>
  );
}
